import React, { useEffect } from 'react';
import { Pencil as CreateOutlinedIcon, Award as CustomerAdminIcon } from 'lucide-react';

import { useAppDispatch, useAppSelector } from 'src/store';
import { getCurrentCustomerId } from 'src/app/customers/state/customersSelector';
import {
    setEditUserFormMode,
    setEditUserFormState,
    updateGeneralUiState,
    updaterUserManagementListFilterState,
} from 'src/app/admin/views/user-management/state/userManagementSlice';
import useAuthService from 'src/shared/hooks/useAuthService';
import useUserManagementList from 'src/app/admin/views/user-management/hooks/useUserManagementList';

import { TertiaryButton } from 'src/shared/components/button/Button';
import { Access, UserType } from 'src/shared/types';
import { DataTableColumns } from 'src/shared/components/data-table/dataTableTypes';
import { DataTableState, TableRowData } from 'src/shared/components/data-table/dataTableTypes';
import DataTable from 'src/shared/components/data-table/DataTable';
import Loader from 'src/shared/components/loader/Loader';
import StatusChip from 'src/shared/components/status-chip/StatusChip';

import UserTypeTag from 'src/app/admin/views/user-management/components/user-type-tag/UserTypeTag';
import UserDetails from 'src/app/admin/views/user-management/components/user-details/UserDetails';
import {
    getUserStatusFromEnum,
    checkIfCustomerAdmin,
    getCustomersForEditUser,
    getProjectsForEditUser,
} from 'src/app/admin/views/user-management/userManagementHelpers';

import styles from './SuperAdminUserManagementList.module.scss';

const tableColumns: DataTableColumns = [
    {
        columnId: 'userId',
        title: 'User Id',
        width: '0',
    },
    {
        columnId: 'customerAdminLogo',
        title: 'Cust. admin',
        width: '70px',
    },
    {
        columnId: 'customerName',
        title: 'Name',
        width: '400px',
    },
    {
        columnId: 'accountStatus',
        title: 'Account Status',
        width: '200px',
    },
    {
        columnId: 'userType',
        title: 'User type',
        width: '108px',
    },
    {
        columnId: 'assignedProjects',
        title: 'Assigned Sites',
        width: '90%',
    },
    {
        columnId: 'actions',
        title: 'Actions',
    },
];

const SuperAdminUserManagementList = () => {
    const currentCustomerId = useAppSelector(getCurrentCustomerId) || '';
    const { isCurrentUserOnboardingManager } = useAuthService();
    const dispatch = useAppDispatch();

    const { isFetchingUsersForSuperAdmin, currentDataForSuperAdmin, sortedUsersForSuperAdmin, tableState, setTableState } =
        useUserManagementList();

    const users = [...(currentDataForSuperAdmin?.users || [])].filter(Boolean);

    const onRowSelect = (rowData: TableRowData) => {
        const userId = rowData[0] as string;
        if (userId) {
            const editUserDetails = users.find(user => user.cognitoId === userId);
            if (!editUserDetails) {
                return;
            }
            // For View formstate - Get Roles, Project list without name and customers without projects
            const userRoles = editUserDetails.customers.find(customer => customer.customerId === currentCustomerId)?.roles;
            const projectsForEditUser = getProjectsForEditUser(editUserDetails.customers);
            const customersForEditUser = getCustomersForEditUser(editUserDetails.customers);
            dispatch(
                setEditUserFormState({
                    ...editUserDetails,
                    projects: projectsForEditUser || [],
                    roles: userRoles ?? [],
                    customers: customersForEditUser || [],
                })
            );

            dispatch(updateGeneralUiState({ shouldShowViewUserModal: true }));
        }
    };

    useEffect(() => {
        if (!currentDataForSuperAdmin) {
            return;
        }

        const tableData: DataTableState['data'] = [];

        sortedUsersForSuperAdmin.forEach(user => {
            const basicDetail = <UserDetails firstName={user.firstName} lastName={user.lastName} email={user.email} status={user.status} />;

            const statusColumn = (
                <StatusChip label={user.status === 'Inactive' ? 'Deactivated' : user.status} status={getUserStatusFromEnum(user.status)} />
            );

            // Display first 3 projects as tag and rest as +X more
            const currentCustomerUserProjects = user.customers.find(customer => customer.customerId === currentCustomerId)?.projects || [];
            const assignedProjects = (
                <div className={styles.projectTags}>
                    {currentCustomerUserProjects.slice(0, 3).map((project, index) => {
                        return (
                            <span key={index} className={`${styles.projectTag} ${styles['projectTag' + user.status]}`}>
                                {project.projectName}
                            </span>
                        );
                    })}
                    {currentCustomerUserProjects.length > 3 && (
                        <span className='bg-grey-20 px-2 py-1 rounded-xl'>+{currentCustomerUserProjects.length - 3}</span>
                    )}
                </div>
            );

            const isCustomerAdminUser = checkIfCustomerAdmin(user.customers, currentCustomerId);
            const customerAdminLogo = isCustomerAdminUser ? <CustomerAdminIcon className={styles.customerAdminLogo} /> : '';

            const userType = user.is_internal_user ? UserType.INTERNAL : UserType.EXTERNAL;
            const userTypeTag = <UserTypeTag userType={userType} />;

            const tableRowData = [user.cognitoId, customerAdminLogo, basicDetail, statusColumn, userTypeTag, assignedProjects];

            const actions = (
                <div className={styles.actionButtons}>
                    <TertiaryButton
                        className={styles.actions}
                        data-testid={`user-management-row-edit-${user.cognitoId}`}
                        onClick={e => {
                            e.stopPropagation();
                            // For Edit formstate - Get Project list without name and customers without projects
                            const projectsForEditUser = getProjectsForEditUser(user.customers);
                            const customersForEditUser = getCustomersForEditUser(user.customers);
                            const userRolesForCustomer = user.customers.find(c => c.customerId === currentCustomerId)?.roles;

                            dispatch(updateGeneralUiState({ shouldShowEditUserModal: true }));
                            dispatch(
                                setEditUserFormState({
                                    ...user,
                                    projects: projectsForEditUser || [],
                                    customers: customersForEditUser || [],
                                    roles: userRolesForCustomer ?? [],
                                })
                            );
                            dispatch(setEditUserFormMode(Access.UPDATE));
                        }}
                    >
                        <CreateOutlinedIcon /> <span>EDIT</span>
                    </TertiaryButton>
                </div>
            );

            //In case of Onboarding Manger - Display Edit option only for the Customer Admin User/Row
            const shouldShowEditButton = isCurrentUserOnboardingManager ? isCustomerAdminUser : true;

            tableRowData.push(shouldShowEditButton ? actions : '');

            tableData.push(tableRowData);
        });

        setTableState(prev => ({
            ...prev,
            data: tableData,
            currentPageNumber: currentDataForSuperAdmin?.pagination.page || 1,
        }));
    }, [currentDataForSuperAdmin]);

    if (!tableState.data || isFetchingUsersForSuperAdmin) {
        return <Loader />;
    }

    const emptyStateMessage = (
        <div className={styles.noRecordsMessage}>
            There were no user accounts found matching this selection. Adjust the filters to find users in your organisation.
        </div>
    );

    const onPageChange = (page: number) => {
        dispatch(updaterUserManagementListFilterState({ currentPageNumber: page }));
    };

    const isTableLoading = tableState.currentPageNumber !== currentDataForSuperAdmin?.pagination.page;

    return (
        <div className={styles.userManagementList} data-testid='user-management-user-table'>
            {isTableLoading && (
                <div className='absolute bg-white opacity-70 size-full z-[2]'>
                    <Loader />
                </div>
            )}
            <DataTable
                className={styles.dataTableStretch}
                state={tableState.data ? tableState : undefined}
                columns={tableColumns}
                onStateChange={setTableState}
                emptyStateMessage={emptyStateMessage}
                onRowSelect={onRowSelect}
                onPageChange={onPageChange}
                totalRowsForPagination={currentDataForSuperAdmin?.pagination.total}
            />
        </div>
    );
};

export default SuperAdminUserManagementList;
